import { ExportAssetType } from '../../../asset/util/useAddPermissions';
import React, { useState } from 'react';
import { Divider, Modal, notification } from 'antd';
import Button from '../../Button';
import {
  AnyAssetApplication,
  executeDownload,
  Flex,
  openNotificationWithIcon,
  useAssetTranslation,
  useCustodianQuery,
} from '@mss/mss-component-library';
import {
  exportAssetTypeHeaders,
  exportAssetTypeSettingsKeys,
  exportAssetTypeToCategory,
  fullAssetName,
} from './util';
import { Header } from './types';
import { Info } from 'react-feather';
import { BrandsSeries } from './BrandsSeries';

type UploadCSVProps<AssetType> = {
  type: ExportAssetType;
  setAssetsFromCsv: React.Dispatch<React.SetStateAction<AssetType[]>>;
  setInvalidKeys: React.Dispatch<React.SetStateAction<string[]>>;
  goToNextStep: () => void;
};

const convertCSVToJSON = (csv: string): Record<string, any>[] => {
  // Replace the Windows CRLF line endings by proper LF line endings
  const crlfToLf = csv.replace(/\r\n/g, '\n');
  const [headerLine, ...lines] = crlfToLf.split('\n');

  const headers = headerLine.split(',');

  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    const lastIndex = line.length - 1;

    for (let i = 0; i <= lastIndex; i++) {
      const char = line[i];

      if (char === '"' && (i === 0 || line[i - 1] === ',')) {
        inQuotes = !inQuotes;
        continue; // Skip the quote character
      }

      if (
        char === '"' &&
        inQuotes &&
        (i === line.length - 1 || line[i + 1] === ',')
      ) {
        inQuotes = !inQuotes;
        continue; // Skip the quote character
      }

      if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  };

  return lines.filter(Boolean).map(line => {
    const values = parseLine(line);
    return values.reduce(
      (acc, currentValue, index) => {
        acc[headers[index]] = currentValue;
        return acc;
      },
      {} as Record<string, any>
    );
  });
};

const parseCSV = (file: File | null): Promise<Record<string, any>[]> => {
  if (!file) throw new Error('No file uploaded');
  const reader = new FileReader();
  reader.readAsText(file);
  return new Promise((resolve, reject) => {
    reader.onload = () => {
      resolve(convertCSVToJSON(reader.result as string));
    };
    reader.onerror = () => {
      reject(reader.error);
    };
  });
};

function validateAndFilterObjectKeys<T>(
  obj: Record<string, any>,
  validKeys: Header[]
): [Partial<T>, string[]] {
  return Object.keys(obj).reduce(
    (acc, currentKey) => {
      if (currentKey === 'undefined') return acc;
      if (validKeys.map(x => x.key).includes(currentKey)) {
        acc[0][currentKey as keyof T] = obj[currentKey] as T[keyof T];
      } else {
        acc[1].push(currentKey);
      }
      return acc;
    },
    [{} as Partial<T>, [] as string[]]
  );
}

type ValidateAssetsResult<T> = {
  invalidKeys: string[];
  assets: T[];
  errors: string[];
};

async function preliminaryValidateAssets<T>(
  data: Record<string, any>[],
  headers: Header[]
): Promise<ValidateAssetsResult<T>> {
  const requiredHeaders = headers.filter(x => x.required).map(x => x.key);
  if (!data.length) {
    return {
      invalidKeys: [],
      assets: [],
      errors: ['The CSV does not contain any data'],
    };
  }
  return data.reduce(
    (acc, obj) => {
      const [asset, invalidKeys] = validateAndFilterObjectKeys(obj, headers);
      acc.invalidKeys.push(...invalidKeys);
      acc.assets.push(asset);
      const missingHeaders = !requiredHeaders.every(x => x in obj);
      if (missingHeaders) {
        acc.errors.push('Missing required headers');
      }
      return acc;
    },
    { invalidKeys: [] as string[], assets: [] as T[], errors: [] as string[] }
  ) as ValidateAssetsResult<T>;
}

function UploadCSV<AssetType extends AnyAssetApplication>({
  type,
  setAssetsFromCsv,
  setInvalidKeys,
  goToNextStep,
}: UploadCSVProps<AssetType>) {
  const { data: custodians } = useCustodianQuery();

  const { t } = useAssetTranslation(['asset', 'assetNetwork', 'common']);
  const ASSET_NS = { ns: 'asset' as const};

  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setProcesssing] = useState<boolean>(false);

  const generateExampleCSV = () => {
    const matchingCSVData = exportAssetTypeHeaders(type);
    const headers = matchingCSVData.map(x => x.key).join(',');
    const exampleData = matchingCSVData.map(x => x.type).join(',');
    return [headers, exampleData].join('\n');
  };

  function downloadCSV(): void {
    const blob = new Blob([generateExampleCSV()], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    executeDownload(url, `example_import_${type}.csv`);
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedFile) {
      setAssetsFromCsv([]);
      setInvalidKeys([]);
    }
    const file = event.target.files ? event.target.files[0] : null;
    setSelectedFile(file);
  };
  const handleUpload = async () => {
    setProcesssing(true);
    const csvData = await parseCSV(selectedFile);

    const validatedAssets = await preliminaryValidateAssets<AssetType>(
      csvData,
      exportAssetTypeHeaders(type)
    );

    if (!validatedAssets.errors.length) {
      setAssetsFromCsv(
        validatedAssets.assets.map(asset => {
          const validCustodians = custodians.map(x => x.options).flat();
          const matchingCustodian = validCustodians.find(
            x => x.value === asset.custodian
          );

          return { ...asset, custodian: matchingCustodian?.value ?? '' };
        })
      );

      setInvalidKeys(Array.from(new Set(validatedAssets.invalidKeys)));
      setProcesssing(false);
      goToNextStep();
    } else {
      setProcesssing(false);
      openNotificationWithIcon(
        notification.error,
        t('Error processing CSV'),
        t(validatedAssets.errors[0])
      );
    }
  };

  const fileSelectedClassNames =
    'border-primary bg-white text-primary hover:bg-primary hover:text-white';
  const noFileSelectedClassNames =
    'border-primary bg-primary text-white hover:bg-primary hover:text-white';

  return (
    <div>
      <div className={isProcessing ? 'opacity-50' : ''}>
        <h4>
          {t('Import ((fullAssetName))s', {
            fullAssetName: fullAssetName(type),
          })}
        </h4>
        <p>
          {t(
            `Here you can upload a CSV file containing (partial) ((category)) information. Please keep in mind that even though the values only have to be partially there, the CSV headers do need to match up to the data structure within this ((category)). Therefore it is advised to download the example file from below.`,
            { ...ASSET_NS, category: exportAssetTypeToCategory(type) }
          )}
        </p>
        {exportAssetTypeSettingsKeys(type).length > 0 && (
          <>
            <p>
              {t(
                `The headers in the CSV file are for all ((type)) types, so not every header is applicable for each type. For your convenience, the table below shown which headers are specific for which type. Filling in the fields for the other types will not have any effect on the import.`,
                { ...ASSET_NS, type: fullAssetName(type) }
              )}
            </p>
            <table className="w-full mt-4">
              <thead>
                <tr className="text-left border-b">
                  <th className="pl-2">{t('Field name')}</th>
                  <th className="capitalize pb-1">
                    {t('((category)) Type', {
                      category: exportAssetTypeToCategory(type),
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {exportAssetTypeSettingsKeys(type).map(
                  ({ field, type }, i, arr) => (
                    <tr
                      key={field}
                      className={`${i === arr.length - 1 ? '' : 'border-b'} ${
                        i % 2 ? '' : 'bg-gray-100'
                      }`}
                    >
                      <td className="italic pl-2 py-1">{field}</td>
                      <td>{type}</td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </>
        )}
        <h5 className="mt-6">{t('Template CSV')}</h5>
        <p className="mb-2">
          {t(
            `Here you can find an template CSV file containing the corresponding headers for ((fullAssetName))s.`,
            { fullAssetName: fullAssetName(type) }
          )}
        </p>
        {type === 'network' && (
          <p className="mb-2">
            {t(
              'Network assets need to have a brand and series/model combination. Not every asset type has the same accepted values.', ASSET_NS
            )}
          </p>
        )}
        {type === 'asset_management' && (
          <p className="mb-2">
            {t(
              'OPC server group is only used for Yokogawa Plant Resource Manager (PRM). You may enter the OPC server group UUID, or reuse the same id and name values as in an exported CSV.',
              ASSET_NS
            )}
          </p>
        )}
        <Flex v="center">
          <Button
            onClick={downloadCSV}
            className="w-1/4"
            ghost
            variant={'primary'}
          >
            {t('Download')}
          </Button>
          {type === 'network' && (
            <>
              <Button
                className="w-1/4 ml-2 border-transparent shadow-none"
                onClick={() => setShowModal(true)}
              >
                <Flex v="center" h="center">
                  <Info className="mr-2" size={14} />
                  {t('List of brands of series/models')}
                </Flex>
              </Button>
              <Modal
                title={t('List of brands of series/models')}
                open={showModal}
                onCancel={() => setShowModal(false)}
                footer={null}
              >
                <BrandsSeries />
              </Modal>
            </>
          )}
        </Flex>
        <h5 className="mt-8">{t('Upload CSV')}</h5>
        <p>
          {t(
            'Select the file you want to upload to the application. After pressing "Upload", the CSV will be processed and validated.', ASSET_NS
          )}
        </p>
        <input
          id="file"
          className="hidden"
          type="file"
          accept=".csv"
          onChange={handleFileChange}
        />
        <label
          htmlFor="file"
          className={`
          cursor-pointer inline-block w-1/4 border transition-colors duration-300 text-center px-4 py-1 ${
            selectedFile ? fileSelectedClassNames : noFileSelectedClassNames
          }
        `}
        >
          {t('Select file')}
        </label>
        <div className="inline-block w-3/4 pl-4 font-bold">
          {selectedFile?.name}
        </div>
      </div>
      <>
        <Divider />
        <Flex h="end">
          <Button
            loading={isProcessing}
            className="w-1/4"
            onClick={handleUpload}
            variant="primary"
            disabled={!selectedFile}
          >
            {t('Upload')}
          </Button>
        </Flex>
      </>
    </div>
  );
}

export default UploadCSV;
